import { useState } from "react";
import { FileText, ExternalLink, Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getLibrary, subtypes, useDrawing } from "@/lib/iwin";

const SYM = { sliding_right: "→", sliding_left: "←", fixed: "F", casement_left: "◁", awning: "△", door_left: "◁", door_right: "▷" };
const Row = ({ k, v }) => <tr className="border-b border-slate-800"><td className="py-1 pr-3 text-slate-400 align-top">{k}</td><td className="py-1 text-slate-100">{v}</td></tr>;

const Pattern = ({ s }) => (
  <div className="flex h-12 w-40 border-2 border-slate-300 bg-slate-900" data-testid="lib-pattern">
    {s.pattern.map((p, i) => <div key={i} className="flex-1 border-r last:border-r-0 border-slate-500 grid place-items-center text-amber-300 text-sm">{SYM[p] || "?"}</div>)}
  </div>
);

const Drawing = ({ s }) => {
  const src = useDrawing(s);
  return (
    <div className="rounded-md bg-white p-1 min-h-[240px] grid place-items-center" data-testid="lib-drawing">
      {src ? <img src={src} alt={`${s.name} section drawing`} className="w-full h-auto" data-testid="lib-drawing-img" /> : <Loader2 className="animate-spin text-cyan-600" />}
    </div>
  );
};

const Subtype = ({ s, lib, onApply, current }) => (
  <div className="space-y-4" data-testid={`lib-subtype-${s.key}`}>
    <div className="flex items-start gap-4">
      <div className="flex-1">
        <h3 className="font-heading text-lg font-semibold">{s.name}</h3>
        <p className="text-xs text-slate-400 mt-1">{s.desc}</p>
      </div>
      <Pattern s={s} />
      {onApply && <button data-testid="lib-apply-btn" onClick={() => onApply(s.key)} className="h-9 px-3 rounded-md bg-amber-400 text-slate-950 text-xs font-semibold flex items-center gap-1.5 hover:bg-amber-300">
        <Check size={14} />{current === s.key ? "Re-apply to item" : "Apply to item"}</button>}
    </div>
    <div className="grid md:grid-cols-2 gap-5 text-xs">
      <table className="w-full" data-testid="lib-tech-table"><tbody>
        <Row k="Frame / sash depth" v={`${s.depth} mm${s.flyscreen_depth ? ` · with flyscreen ${s.flyscreen_depth} mm` : " · no flyscreen option"}`} />
        <Row k="Glass groove SGU" v={`${lib.glazing.SGU.groove} mm · gaskets for ${lib.glazing.SGU.glass.join(" / ")} mm`} />
        <Row k="Glass groove DGU" v={`${lib.glazing.DGU.groove} mm · gaskets for ${lib.glazing.DGU.glass.join(" / ")} mm`} />
        <Row k="Frame joint" v={s.joint} />
        <Row k="Max design / sash load" v={`${s.max_leaf_kg} kg per leaf`} />
        {s.lock && <Row k="Lock" v={s.lock} />}
        {s.tracks > 0 && <Row k="Tracks" v={s.tracks} />}
        {s.interlocks.length > 0 && <Row k="Interlock stiles" v={s.interlocks.map((k) => `${k}-type`).join(", ")} />}
        <Row k="Sightlines (drafting)" v={`frame ${s.frame} · mullion/interlock ${s.mullion} · sash ${s.sash} mm`} />
        {s.railing && <Row k="Integrated railing" v={`${s.railing.bottom} mm above sill · min ${s.railing.height} mm · handrail ${s.railing.handrail} mm`} />}
        <Row k="Alloy" v="6063-T6" />
      </tbody></table>
      <div>
        <table className="w-full" data-testid="lib-limits-table">
          <thead><tr className="text-slate-400 text-left"><th className="pb-1">Wind load</th><th>Width</th><th>Height</th></tr></thead>
          <tbody>{Object.entries(s.limits).map(([wl, l]) => (
            <tr key={wl} className="border-b border-slate-800"><td className="py-1 text-amber-300">{wl} Pa{wl === "2500" ? " · 350 Pa water" : wl === "1200" && s.family === "sliding_big" ? " · 200 Pa water" : ""}</td>
              <td>{l.wmin ? `${l.wmin}–` : "≤ "}{l.wmax || "—"}</td><td>{l.hmin ? `${l.hmin}–` : "≤ "}{l.hmax || "—"}{l.note ? ` (${l.note})` : ""}</td></tr>
          ))}</tbody>
        </table>
        <div className="flex flex-wrap gap-2 mt-3">
          <a href={s.section_pdf} target="_blank" rel="noreferrer" data-testid="lib-section-pdf-link" className="h-7 px-2 rounded border border-slate-700 flex items-center gap-1 hover:border-cyan-500"><FileText size={12} />Section drawing PDF</a>
          <a href={s.size_graph} target="_blank" rel="noreferrer" data-testid="lib-size-graph-link" className="h-7 px-2 rounded border border-slate-700 flex items-center gap-1 hover:border-cyan-500"><FileText size={12} />Size limitation graph</a>
          <a href={s.page} target="_blank" rel="noreferrer" className="h-7 px-2 rounded border border-slate-700 flex items-center gap-1 hover:border-cyan-500"><ExternalLink size={12} />Product page</a>
        </div>
      </div>
    </div>
    <Drawing s={s} />
  </div>
);

const Interlocks = ({ lib }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="lib-interlocks">
    {Object.entries(lib.interlocks).map(([k, v]) => (
      <div key={k} className="rounded-md border border-slate-700 p-2" data-testid={`lib-interlock-${k}`}>
        <img src={v.images.SGU} alt={`${v.label} SGU`} className="w-full h-36 object-contain bg-white rounded" loading="lazy" />
        <p className="text-sm font-semibold mt-1">{v.label} <span className="text-[10px] text-slate-400">{v.range === "big" ? "big openings" : "standard"}</span></p>
        <p className="text-[11px] text-slate-400">{v.desc}</p>
        <a href={v.images.DGU} target="_blank" rel="noreferrer" className="text-[10px] text-cyan-300">DGU version ↗</a>
      </div>
    ))}
  </div>
);

const Accessories = ({ lib }) => (
  <div className="grid md:grid-cols-2 gap-2" data-testid="lib-accessories">
    {lib.accessories.map((a) => (
      <div key={a.key} className="rounded-md border border-slate-700 p-2.5" data-testid={`lib-acc-${a.key}`}>
        <p className="text-sm font-semibold">{a.name} <span className="text-[10px] text-amber-300">{a.for.join(" · ")}</span></p>
        <p className="text-[11px] text-slate-300">{a.desc}</p>
        {a.material && <p className="text-[10px] text-slate-500">Material: {a.material}</p>}
      </div>
    ))}
    <table className="md:col-span-2 text-xs mt-2" data-testid="lib-performance"><tbody>{lib.performance.map(([k, v]) => <Row key={k} k={k} v={v} />)}</tbody></table>
  </div>
);

const NavBtn = ({ on, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`w-full text-left px-2 py-1 rounded text-xs transition-colors ${on ? "bg-amber-400/15 text-amber-200" : "text-slate-300 hover:bg-slate-800"}`}>{children}</button>
);

export const LibraryDialog = ({ current, onApply, onClose }) => {
  const lib = getLibrary();
  const [sel, setSel] = useState(current || "s_2t2s");
  const s = subtypes().find((x) => x.key === sel);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl h-[88vh] bg-[#111827] border-slate-700 text-slate-100 flex flex-col" data-testid="library-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">IWIN-S product library</DialogTitle>
          <DialogDescription className="text-slate-400">{lib.brand} · full range with technical data, size limits, official section drawings, interlocks & accessories</DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 flex gap-4">
          <nav className="w-60 shrink-0 overflow-y-auto space-y-3 pr-1" data-testid="library-nav">
            {lib.families.map(([f, label]) => (
              <div key={f}><p className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</p>
                {subtypes().filter((x) => x.family === f).map((x) => <NavBtn key={x.key} testid={`lib-nav-${x.key}`} on={sel === x.key} onClick={() => setSel(x.key)}>{x.name}{current === x.key ? " ●" : ""}</NavBtn>)}
              </div>
            ))}
            <div><p className="font-mono text-[10px] uppercase tracking-wider text-slate-500 mb-1">Components</p>
              <NavBtn testid="lib-nav-interlocks" on={sel === "interlocks"} onClick={() => setSel("interlocks")}>Interlock stiles A–F</NavBtn>
              <NavBtn testid="lib-nav-accessories" on={sel === "accessories"} onClick={() => setSel("accessories")}>Accessories & performance</NavBtn>
            </div>
          </nav>
          <div className="flex-1 min-w-0 overflow-y-auto pr-1">
            {s ? <Subtype s={s} lib={lib} onApply={onApply} current={current} /> : sel === "interlocks" ? <Interlocks lib={lib} /> : <Accessories lib={lib} />}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
